// Phone numbers on the start form: read in the chosen country (or from a leading +), checked
// against that country's numbering plan, shown in its local style, and sent as E.164
// (+12265551234), which is how the API saves it. The "min" metadata here checks each country's
// lengths and prefixes (about 20 KB lighter than "max"); the API checks every number against the
// full plan, and its refusal (invalid_phone) shows on the same field.

import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/min";

export type { CountryCode };

const ALL = new Set<string>(getCountries());

/** The visitor's country from their browser languages (en-CA → CA), else US. */
export function defaultCountry(languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages ?? [navigator.language]): CountryCode {
  for (const tag of languages) {
    const region = tag?.split("-").find((part, i) => i > 0 && /^[A-Za-z]{2}$/.test(part))?.toUpperCase();
    if (region && ALL.has(region)) return region as CountryCode;
  }
  return "US";
}

const parse = (text: string, country: CountryCode) => parsePhoneNumberFromString(text.trim(), { defaultCountry: country });

/** E.164 when it's a real number (in `country`, unless it starts with +), else null. */
export function toE164(text: string, country: CountryCode): string | null {
  const p = parse(text, country);
  return p?.isValid() && !p.ext ? p.number : null; // the API refuses an extension too
}

/** For a valid number: its country and its local formatting ("(226) 687-4999"), to show after typing. */
export function tidy(text: string, country: CountryCode): { country: CountryCode; text: string } | null {
  const p = parse(text, country);
  if (!p?.isValid() || p.ext) return null;
  const c = (p.country ?? country) as CountryCode;
  return { country: c, text: p.formatNational() };
}

/** While typing "+44 …": the country the digits so far belong to, if they say. */
export function countryOfInternational(text: string): CountryCode | undefined {
  if (!text.trim().startsWith("+")) return undefined;
  const typer = new AsYouType();
  typer.input(text);
  return typer.getCountry();
}

export const dialCode = (c: CountryCode) => `+${getCountryCallingCode(c)}`;

/** 🇨🇦 from CA (regional indicator letters; shown as letters where a system has no flags). */
export const flag = (c: string) => String.fromCodePoint(...[...c.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));

const PINNED: CountryCode[] = ["US", "CA", "GB", "AU", "NZ", "IE"];

/** Every country, by name, with the most likely ones first. */
export function countryOptions(first: CountryCode): { value: CountryCode; label: string }[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    names = null;
  }
  const label = (c: CountryCode) => `${names?.of(c) ?? c} (${dialCode(c)})`;
  const top = [first, ...PINNED.filter((c) => c !== first)];
  const rest = (getCountries() as CountryCode[]).filter((c) => !top.includes(c)).sort((a, b) => label(a).localeCompare(label(b)));
  return [...top, ...rest].map((c) => ({ value: c, label: label(c) }));
}

/** A saved number (E.164, or anything older) for display: "+1 226 687 4999", or as it was. */
export function formatPhone(saved: string): string {
  const p = parsePhoneNumberFromString(saved);
  return p?.isValid() ? p.formatInternational() : saved;
}
